import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, X, ArrowRight, AlertCircle, Clock, CheckCircle, 
  AlertTriangle 
} from 'lucide-react';
import { apiFetch } from '../../lib/apiClient';
import { useAuth } from '../../contexts/AuthContext';

export default function FloatingTicketNotifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [dismissingIds, setDismissingIds] = useState(new Set());

  useEffect(() => {
    let active = true;
    const loadNotifications = async () => {
      if (!user || user.user_type === 'affiliate') return;
      try {
        const res = await apiFetch('/api/tickets/notifications/pending');
        if (res.ok && active) {
          const data = await res.json();
          setNotifications(data.notifications || []);
        }
      } catch (err) {
        console.warn('Error fetching floating ticket notifications:', err);
      }
    };

    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [user]);

  const handleDismiss = async (e, notif) => {
    e.stopPropagation();
    setDismissingIds(prev => new Set(prev).add(notif.notif_id));
    try {
      await apiFetch(`/api/tickets/notifications/${notif.notif_id}/dismiss`, {
        method: 'POST'
      });
      setNotifications(prev => prev.filter(n => n.notif_id !== notif.notif_id));
    } catch (err) {
      console.error('Error dismissing notification:', err);
    } finally {
      setDismissingIds(prev => {
        const next = new Set(prev);
        next.delete(notif.notif_id);
        return next;
      });
    }
  };

  const handleOpenTicket = (ticketId) => {
    navigate(`/tickets?id=${ticketId}`);
  };

  if (!notifications.length) return null;

  return (
    <aside aria-label="Notificaciones de tickets" className="fixed top-5 right-5 z-[9999] flex flex-col gap-3 max-w-sm w-full pointer-events-none">
      {notifications.map((notif) => {
        const isRequiereRespuesta = notif.estado === 'requiere_respuesta';
        const isAtascado = notif.estado === 'atascado';
        const isRevisar = notif.estado === 'revisar';
        const isNuevo = notif.estado === 'nuevo';

        return (
          <div
            key={notif.notif_id}
            role="alert"
            className={`pointer-events-auto relative overflow-hidden rounded-2xl bg-white border shadow-2xl p-4 transition-all duration-300 transform translate-y-0 ${
              isRevisar
                ? 'border-purple-400 ring-2 ring-purple-400/50 bg-gradient-to-br from-purple-50/90 via-white to-indigo-50/50'
                : isRequiereRespuesta
                ? 'border-amber-400 ring-2 ring-amber-400/50 bg-gradient-to-br from-amber-50/90 via-white to-orange-50/50'
                : isAtascado
                ? 'border-rose-400 ring-2 ring-rose-400/40 bg-gradient-to-br from-rose-50/90 via-white to-red-50/40'
                : 'border-slate-200 hover:border-indigo-300'
            }`}
          >
            {/* Header del Toast */}
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-black tracking-wide bg-indigo-100 text-indigo-800">
                  <Bell className="w-3 h-3 animate-bounce text-indigo-600" />
                  {notif.folio}
                </span>

                {isRevisar ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-black uppercase tracking-wider bg-purple-600 text-white shadow-sm">
                    <CheckCircle className="w-3 h-3" />
                    Por Revisar
                  </span>
                ) : isRequiereRespuesta ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-black uppercase tracking-wider bg-amber-500 text-white shadow-sm animate-pulse">
                    <AlertTriangle className="w-3 h-3" />
                    Requiere Respuesta
                  </span>
                ) : isAtascado ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-rose-500 text-white">
                    <AlertCircle className="w-3 h-3" />
                    Atascado
                  </span>
                ) : isNuevo ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-100 text-blue-800">
                    <Clock className="w-3 h-3" />
                    Nuevo
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800">
                    <CheckCircle className="w-3 h-3" />
                    En Proceso
                  </span>
                )}
              </div>

              {/* Botón Cerrar X */}
              <button
                type="button"
                onClick={(e) => handleDismiss(e, notif)}
                disabled={dismissingIds.has(notif.notif_id)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                title="Cerrar notificación"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Asunto y Peticionario */}
            <h4 className="text-sm font-bold text-slate-900 leading-snug line-clamp-2 mb-1.5">
              {notif.titulo}
            </h4>

            <div className="flex items-center justify-between text-xs text-slate-600 mb-3">
              <span className="truncate">
                <span className="font-semibold text-slate-500">Solicitó:</span>{' '}
                <span className="font-bold text-slate-800">{notif.creador_nombre || 'Equipo'}</span>
              </span>
              {notif.prioridad && (
                <span className={`font-semibold capitalize px-1.5 py-0.2 rounded text-[11px] ${
                  notif.prioridad === 'urgente' ? 'text-red-700 bg-red-100' :
                  notif.prioridad === 'alta' ? 'text-amber-700 bg-amber-100' :
                  'text-slate-600 bg-slate-100'
                }`}>
                  {notif.prioridad}
                </span>
              )}
            </div>

            {/* Si requiere respuesta, advertir sutilmente sobre recordatorio */}
            {isRequiereRespuesta && (
              <p className="text-[11px] text-amber-700 bg-amber-100/70 rounded-md px-2 py-1 mb-2.5 flex items-center gap-1 font-medium">
                <Clock className="w-3 h-3 flex-shrink-0" />
                Si lo cierras, reaparecerá cada 4 horas hasta que se responda.
              </p>
            )}

            {/* Botón Ver Ticket */}
            <button
              type="button"
              onClick={() => handleOpenTicket(notif.ticket_id)}
              className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-slate-900 hover:bg-indigo-600 text-white text-xs font-bold rounded-xl shadow-md transition-all cursor-pointer group"
            >
              <span>Abrir y Atender Ticket</span>
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        );
      })}
    </aside>
  );
}
